import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Check, ShoppingCart, X, Phone, Mail, Award, Shield, Sparkles } from "lucide-react";
import LazyImage from "@/components/LazyImage";
import QuoteModal from "@/components/QuoteModal";
import useQuoteModal from "@/hooks/useQuoteModal";
import ProductNavigation from "@/components/ui/ProductNavigation";



const ProductDetail = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);

  const productData = useMemo(() => ({
    dollaro: {
      id: "dollaro",
      name: t('productDetail.dollaro.name'),
      description: t('productDetail.dollaro.description'),
      longDescription: t('productDetail.dollaro.longDescription'),
      features: [
        t('productDetail.dollaro.feature1'),
        t('productDetail.dollaro.feature2'),
        t('productDetail.dollaro.feature3'),
        t('productDetail.dollaro.feature4'),
        t('productDetail.dollaro.feature5')
      ],
      specifications: [
        { name: t('productDetail.dollaro.spec1.name'), value: t('productDetail.dollaro.spec1.value') },
        { name: t('productDetail.dollaro.spec2.name'), value: t('productDetail.dollaro.spec2.value') },
        { name: t('productDetail.dollaro.spec3.name'), value: t('productDetail.dollaro.spec3.value') },
        { name: t('productDetail.dollaro.spec4.name'), value: t('productDetail.dollaro.spec4.value') },
        { name: t('productDetail.dollaro.spec5.name'), value: t('productDetail.dollaro.spec5.value') }
      ],
      applications: [
        t('productDetail.dollaro.app1'),
        t('productDetail.dollaro.app2'),
        t('productDetail.dollaro.app3'),
        t('productDetail.dollaro.app4'),
        t('productDetail.dollaro.app5')
      ],
      colors: [
        { name: t('productDetail.dollaro.color1'), hex: "#1a1a1a" },
        { name: t('productDetail.dollaro.color2'), hex: "#f5f5f0" },
        { name: t('productDetail.dollaro.color3'), hex: "#c68c53" },
        { name: t('productDetail.dollaro.color4'), hex: "#c0c0c0" },
        { name: t('productDetail.dollaro.color5'), hex: "#800020" },
        { name: t('productDetail.dollaro.color6'), hex: "#000080" }
      ],
      mainImage: "/images/secciones-images/Dollaro-Nappa/dn1.webp",
      gallery: [
        "/images/secciones-images/Dollaro-Nappa/dn1.webp",
        "/images/secciones-images/Dollaro-Nappa/dn2.webp",
        "/images/secciones-images/Dollaro-Nappa/dn3.webp",
        "/images/secciones-images/Dollaro-Nappa/dn4.webp"
      ],
      rating: 4.8,
      reviews: 124,
      benefits: [
        {
          title: t('productDetail.dollaro.benefit1.title'),
          description: t('productDetail.dollaro.benefit1.description'),
          icon: Sparkles
        },
        {
          title: t('productDetail.dollaro.benefit2.title'),
          description: t('productDetail.dollaro.benefit2.description'),
          icon: Shield
        },
        {
          title: t('productDetail.dollaro.benefit3.title'),
          description: t('productDetail.dollaro.benefit3.description'),
          icon: Check
        },
        {
          title: t('productDetail.dollaro.benefit4.title'),
          description: t('productDetail.dollaro.benefit4.description'),
          icon: Award
        }
      ]
    }
  }), [t]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [activeTab, setActiveTab] = useState("descripcion");

  // ✅ AGREGA ESTO:
  const {
    selectedProduct,
    openQuoteModal,
    closeQuoteModal,
    phoneNumbers,
    getWhatsAppUrl,
  } = useQuoteModal();

  // Cargar los datos del producto
  useEffect(() => {
    // En un caso real, aquí harías una llamada a una API
    setProduct(productData.dollaro);
  }, [productData]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
        <p className="mb-6 text-gray-600">
          Lo sentimos, el producto que buscas no está disponible.
        </p>
        <Link to="/" className="text-red-600 hover:text-red-800 font-medium">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <ProductNavigation />

      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        {/* Background Image with lazy loading */}
        <div className="absolute inset-0">
          {" "}
          <LazyImage
            src="/images/home/detallesdollaro.webp"
            alt={t('productDetail.hero.altText')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 h-full flex items-center relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
                {t('productDetail.hero.title')}
              </h1>
              <div className="w-20 h-1 bg-red-600 mb-6"></div>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {t('productDetail.hero.description')}
              </p>
              <div className="flex items-center space-x-4">
                <motion.button
                  onClick={() => openQuoteModal(product)}
                  className="bg-red-600 hover:bg-red-700 text-white py-3 px-8 rounded-full font-medium flex items-center space-x-2 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ShoppingCart className="h-5 w-5" />
                  <span>{t('productDetail.requestInfoBtn')}</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section
        id="detalles"
        className="py-20 bg-gradient-to-b from-black to-gray-900"
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column - Gallery */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="sticky top-8"
              >
                <div className="relative aspect-square overflow-hidden rounded-xl mb-4">
                  <LazyImage
                    key={selectedImage}
                    src={product.gallery[selectedImage] || product.mainImage}
                    alt={`${product.name} - ${t('productDetail.galleryView')} ${selectedImage + 1}`}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {t('productDetail.premiumBadge')}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {product.gallery.map((image, index) => (
                    <motion.div
                      key={index}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer ${selectedImage === index ? "ring-2 ring-red-600" : ""
                        }`}
                      onClick={() => setSelectedImage(index)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LazyImage
                        src={image || product.mainImage}
                        alt={`${product.name} - ${t('productDetail.galleryView')} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      <span className="ml-1 font-medium">{product.rating}</span>
                    </div>
                    <span className="mx-2 text-gray-500">•</span>
                    <span className="text-gray-400">
                      {product.reviews} {t('productDetail.reviewsLabel', { count: product.reviews })}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-4">
                    {t('productDetail.availableColors')}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((color, index) => (
                      <motion.div
                        key={index}
                        className={`w-10 h-10 rounded-full cursor-pointer ${selectedColor === index
                          ? "ring-2 ring-red-600 ring-offset-2 ring-offset-gray-800"
                          : ""
                          }`}
                        style={{ backgroundColor: color.hex }}
                        onClick={() => setSelectedColor(index)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    {t('productDetail.selectedColor')}:{" "}
                    <span className="text-white">
                      {product.colors[selectedColor].name}
                    </span>
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold mb-6">{product.name}</h2>

                {/* Tabs */}
                <div className="border-b border-gray-800 mb-8">
                  <div className="flex overflow-x-auto space-x-4 sm:space-x-8">
                    {[
                      { id: "descripcion", name: t('productDetail.tabs.description') },
                      { id: "especificaciones", name: t('productDetail.tabs.specifications') },
                      { id: "aplicaciones", name: t('productDetail.tabs.applications') },
                      { id: "beneficios", name: t('productDetail.tabs.benefits') },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`pb-4 px-2 font-medium transition-colors ${activeTab === tab.id
                          ? "text-red-600 border-b-2 border-red-600"
                          : "text-gray-400 hover:text-white"
                          }`}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="min-h-[400px]">
                  {/* Descripción */}
                  <AnimatePresence mode="wait">
                    {activeTab === "descripcion" && (
                      <motion.div
                        key="descripcion"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <p className="text-gray-300 mb-6 leading-relaxed">
                          {product.longDescription}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                          {product.features.map((feature, index) => (
                            <div key={index} className="flex items-start">
                              <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-gray-300">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="mt-12 relative overflow-hidden rounded-xl aspect-video">
                          <video
                            className="w-full h-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                            onEnded={() => {
                              const video = document.querySelector("video");
                              if (video) {
                                video.currentTime = 0;
                                video.play();
                              }
                            }}
                          >
                            <source
                              src="/images/secciones-images/Dollaro-Nappa/muebles.webm"
                              type="video/webm"
                            />
                          </video>
                        </div>
                      </motion.div>
                    )}

                    {/* Especificaciones */}
                    {activeTab === "especificaciones" && (
                      <motion.div
                        key="especificaciones"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="bg-gray-800 rounded-xl p-8">
                          <h3 className="text-2xl font-bold mb-6">
                            {t('productDetail.specs.title')}
                          </h3>
                          <div className="space-y-6">
                            {product.specifications.map((spec, index) => (
                              <div
                                key={index}
                                className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-gray-700"
                              >
                                <div className="text-gray-400 mb-2 md:mb-0">
                                  {spec.name}
                                </div>
                                <div className="text-xl font-semibold">
                                  {spec.value}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-8 bg-gradient-to-r from-red-900/30 to-red-600/30 rounded-lg p-6">
                            <h4 className="flex items-center text-lg font-semibold mb-4">
                              <Shield className="h-5 w-5 mr-2 text-red-500" />
                              {t('productDetail.specs.certificationsTitle')}
                            </h4>
                            <ul className="space-y-2 text-gray-300">
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>
                                  {t('productDetail.specs.certFireResistance')}
                                </span>
                              </li>
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>
                                  {t('productDetail.specs.certISO')}
                                </span>
                              </li>
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>
                                  {t('productDetail.specs.certDurabilityTests')}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Aplicaciones */}
                    {activeTab === "aplicaciones" && (
                      <motion.div
                        key="aplicaciones"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6"
                      >
                        {product.applications.map((application, index) => {
                          // Mapa de aplicaciones a imágenes usando el índice
                          const appImages = [
                            "sofaysillonesdealtagama.webp",
                            "cabeceras.webp",
                            "hoteleria.webp",
                            "restaurantes.webp",
                            "oficina.webp"
                          ];

                          const imageName = appImages[index] || "dn.webp";

                          return (
                            <motion.div
                              key={index}
                              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden"
                              whileHover={{
                                y: -5,
                                boxShadow:
                                  "0 20px 25px -5px rgba(220, 38, 38, 0.2)",
                              }}
                            >
                              <LazyImage
                                src={`/images/secciones-images/Dollaro-Nappa/${imageName}`}
                                alt={application}
                                className="h-60 w-full object-cover object-center"
                              />
                              <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">
                                  {application}
                                </h3>
                                <p className="text-gray-400">
                                  {t('productDetail.applications.description', { application: application.toLowerCase() })}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}

                    {/* Beneficios */}
                    {activeTab === "beneficios" && (
                      <motion.div
                        key="beneficios"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {product.benefits.map((benefit, index) => (
                            <motion.div
                              key={index}
                              className="bg-gray-800 rounded-xl p-6"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: { delay: index * 0.1 },
                              }}
                              whileHover={{
                                y: -5,
                                boxShadow:
                                  "0 20px 25px -5px rgba(220, 38, 38, 0.2)",
                                transition: {
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 10,
                                },
                              }}
                            >
                              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
                                <benefit.icon className="h-6 w-6 text-red-500" />
                              </div>
                              <h3 className="text-xl font-bold mb-2">
                                {benefit.title}
                              </h3>
                              <p className="text-gray-400">
                                {benefit.description}
                              </p>
                            </motion.div>
                          ))}
                        </div>

                        <div className="mt-12 bg-gradient-to-r from-red-900 to-red-700 rounded-xl p-8">
                          <div className="flex flex-col md:flex-row items-center">
                            <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
                              <h3 className="text-2xl font-bold mb-4">
                                {t('productDetail.benefits.ctaTitle')}
                              </h3>
                              <p className="text-gray-200 mb-6">
                                {t('productDetail.benefits.ctaDescription')}
                              </p>
                              <motion.button
                                onClick={() => openQuoteModal(product)}
                                className="bg-white text-red-600 hover:bg-gray-100 py-3 px-8 rounded-full font-medium flex items-center space-x-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <ShoppingCart className="h-5 w-5" />
                                <span>{t('productDetail.requestInfoBtn')}</span>
                              </motion.button>
                            </div>
                            <div className="md:w-1/3 flex justify-center">
                              <motion.div
                                animate={{
                                  rotate: [0, 5, -5, 5, 0],
                                  y: [0, -5, 5, -5, 0],
                                }}
                                transition={{
                                  repeat: Infinity,
                                  duration: 5,
                                  ease: "easeInOut",
                                }}
                              >
                                <LazyImage
                                  src="https://images.unsplash.com/photo-1567016432779-094069958ea5?q=80&w=1780"
                                  alt="Furniture"
                                  className="w-40 h-40 object-contain"
                                />
                              </motion.div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* ✅ AGREGA ESTO AL FINAL */}
      <QuoteModal
        isOpen={!!selectedProduct}
        onClose={closeQuoteModal}
        product={selectedProduct}
        selectedColor={selectedColor}
        phoneNumbers={phoneNumbers}
        getWhatsAppUrl={getWhatsAppUrl}
      />
    </div>
  );
};

export default ProductDetail;
